import { Expense, ExpenseKindEnum, ExpenseStatusEnum, SubscriptionTypeEnum } from "@finway-group/shared/lib/models"
import { useInfiniteQuery } from "@tanstack/react-query"
import { queryClient } from "~Shared/config/reactQueryConfig"
import { MODEL, STALE_TIME_ANALYTICS_MS } from "~Shared/queries/analytics/consts"
import { CacheEnum, type QueryParams } from "~Shared/queries/consts"
import { queryPost } from "~Shared/utils/reactQuery.utils"

const PAGE_SIZE = 10

export const PERFORMANCE_EXPENSES_TAB_KEYS = {
    PAID: "expenses-paid",
    PENDING: "expenses-pending",
    PO_PENDING: "purchase-orders-pending",
} as const

export type PerformanceExpensesTabKey = (typeof PERFORMANCE_EXPENSES_TAB_KEYS)[keyof typeof PERFORMANCE_EXPENSES_TAB_KEYS]

const QUERY_PARAMS: Record<PerformanceExpensesTabKey, QueryParams> = {
    [PERFORMANCE_EXPENSES_TAB_KEYS.PAID]: {
        filter: {
            kind: { ne: ExpenseKindEnum.TRIP_FOLDER },
            status: { in: [ExpenseStatusEnum.PAID, ExpenseStatusEnum.DONE] },
            subscriptionType: { ne: SubscriptionTypeEnum.INITIAL_REQUEST },
        },
    },
    [PERFORMANCE_EXPENSES_TAB_KEYS.PENDING]: {
        filter: {
            kind: { ne: ExpenseKindEnum.TRIP_FOLDER },
            reportingOnlyExpenseForPoId: { set: false },
            status: {
                in: [ExpenseStatusEnum.PURCHASE_PENDING, ExpenseStatusEnum.DOCS_NEEDED, ExpenseStatusEnum.INVOICE_PENDING, ExpenseStatusEnum.APPROVED, ExpenseStatusEnum.REVIEWED],
            },
        },
    },
    [PERFORMANCE_EXPENSES_TAB_KEYS.PO_PENDING]: {
        filter: {
            kind: { ne: ExpenseKindEnum.TRIP_FOLDER },
            reportingOnlyExpenseForPoId: { set: true },
            status: {
                in: [ExpenseStatusEnum.PURCHASE_PENDING, ExpenseStatusEnum.DOCS_NEEDED, ExpenseStatusEnum.INVOICE_PENDING, ExpenseStatusEnum.APPROVED, ExpenseStatusEnum.REVIEWED],
            },
            deleted: { is: false },
            subscriptionType: { ne: SubscriptionTypeEnum.INITIAL_REQUEST },
        },
    },
}

type PerformanceExpensesPage = {
    docs: Array<Expense>
    totalPages: number
    totalDocs: number
}

type PerformanceExpensesPayload = { startDate: string; endDate: string }

export const usePerformanceExpensesInfinite = ({
    costCenterId,
    tabKey,
    startDate,
    endDate,
    shouldRefetch,
    enabled,
}: {
    costCenterId: string
    tabKey: PerformanceExpensesTabKey
    startDate: string
    endDate: string
    shouldRefetch: boolean
    enabled: boolean
}) => {
    const { filter } = QUERY_PARAMS[tabKey]

    return useInfiniteQuery({
        queryKey: [CacheEnum.ANALYTICS, "performance-expenses", costCenterId, tabKey, startDate, endDate, shouldRefetch],
        queryFn: async ({ signal, pageParam = 1 }) => {
            const path = `${MODEL}/costcenters/${costCenterId}/performance-expenses`
            const data = await queryPost<PerformanceExpensesPayload, PerformanceExpensesPage>(
                path,
                { startDate, endDate },
                { signal, params: { page: pageParam, limit: PAGE_SIZE, ...filter } },
            )
            return {
                docs: data.docs.map((e) => new Expense(e)),
                totalPages: data.totalPages,
                totalDocs: data.totalDocs,
            }
        },
        getNextPageParam: (lastPage, allPages) => (allPages.length < lastPage.totalPages ? allPages.length + 1 : undefined),
        staleTime: STALE_TIME_ANALYTICS_MS,
        keepPreviousData: true,
        enabled: enabled && !!costCenterId && !!startDate && !!endDate,
    })
}

export const invalidatePerformanceExpenses = async (costCenterIds: Array<String>) => {
    if (!costCenterIds?.length) {
        return
    }
    await Promise.all(
        costCenterIds.map((ccId) =>
            queryClient.invalidateQueries({
                queryKey: [CacheEnum.ANALYTICS, "performance-expenses", ccId],
            }),
        ),
    )
}
